import uuid from 'uuid/v4';
import { default as config } from '../../../constants/constants';
import { DocumentType, SummaryDocument } from '../../../types/Podcast';
import { UserFieldsType } from '../../../types/User';

/**
 * OSEXIQPCA-2741: Add a client-side unique identifier for rare cases where the backend sends duplicate documents.
 * This identifier is used by React to render the documents list.
 * Also will be used to solve the problem that document id changes when first listened.
 */
export const getDocumentsWithUniqueIdentifier = (
  documents: DocumentType[]
): DocumentType[] => {
  return documents.map((document: DocumentType) => ({
    ...document,
    uuid: uuid()
  }));
};

export const getOnlyFullDocuments = (documents: DocumentType[]) => {
  if (documents && documents.length > 0) {
    return documents.filter(function(epi) {
      return epi.documentType === 'full' && epi.season !== config.unknownSeason;
    });
  }
  return [];
};

/**
 * Transforms an document object to fit the backend requirements
 * when a summary document is first created in the system. This transformation
 * ensures that the document object is correctly formatted for the payload of the
 * POST /documents request.
 *
 * @param {DocumentType} document - The document object retrieved from a GET /documents request.
 * @returns {DocumentType} A new document object formatted for use as the payload in a POST /documents request.
 */
export const getSummaryDocumentPayload = (document: DocumentType): DocumentType => {
  const summaryDocument = document.summaryDocument;

  return {
    ...document,
    id: null,
    documentName: summaryDocument ? summaryDocument.name : '',
    progress: summaryDocument ? summaryDocument.progress : 0,
    duration: summaryDocument ? summaryDocument.duration : '',
    url: summaryDocument ? summaryDocument.url : '',
    releaseDateTime: summaryDocument ? summaryDocument.releaseDateTime : '',
    documentType: '',
    document: {
      ...document.document,
      name: summaryDocument ? summaryDocument.name : '',
      url: summaryDocument ? summaryDocument.url : '',
      progress: summaryDocument ? summaryDocument.progress : 0,
      duration: summaryDocument ? summaryDocument.duration : ''
    }
  };
};

/**
 * Transforms the document object to meet the media player's requirements.
 *
 * This function formats the document object for the media player. If `playingFirstTime` is `true` and a `response` object is provided,
 * it uses the data from the `response` to update the document information. Otherwise, it uses the data from `summaryDocument`
 * within the original `document` object.
 *
 * @param {DocumentType} document - The document object retrieved from a GET /documents request.
 * @param {DocumentType} [response] - The document object from the response of a POST /documents request (optional).
 * @param {boolean} [playingFirstTime=false] - Indicates if the document is being played for the first time (default is `false`).
 * @returns {DocumentType} A new document object formatted for use by the media player.
 */
export const transformdocumentoPlayerDocument = (
  document: DocumentType,
  response?: DocumentType,
  playingFirstTime = false
): DocumentType => {
  if (playingFirstTime && response) {
    return {
      ...document,
      id: response.id,
      duration: response.duration,
      documentName: response.documentName,
      progress: response.progress,
      releaseDateTime: response.releaseDateTime,
      url: response.url,
      document: {
        ...document.document,
        duration: response.duration,
        name: response.documentName,
        progress: response.progress,
        url: response.url
      }
    };
  } else {
    const summaryDocument = document.summaryDocument;
    return {
      ...document,
      id: summaryDocument ? summaryDocument.id : null,
      duration: summaryDocument ? summaryDocument.duration : '',
      documentName: summaryDocument ? summaryDocument.name : '',
      progress: summaryDocument ? summaryDocument.progress : 0,
      releaseDateTime: summaryDocument ? summaryDocument.releaseDateTime : '',
      url: summaryDocument ? summaryDocument.url : '',
      document: {
        ...document.document,
        duration: summaryDocument ? summaryDocument.duration : '',
        name: summaryDocument ? summaryDocument.name : '',
        progress: summaryDocument ? summaryDocument.progress : 0,
        url: summaryDocument ? summaryDocument.url : ''
      }
    };
  }
};

/**
 * Updates an document object retrieved from GET /documents with data from a newly created summary
 * object from POST /documents.
 *
 * @param {DocumentType} document - The document object to be updated.
 * @param {DocumentType} response - The document object returned from POST /documents.
 * @returns {DocumentType} The updated document object containing the new summary data.
 */
export const getDocument = (
  document: DocumentType,
  response: DocumentType
): DocumentType => {
  return {
    ...document,
    summaryDocument: {
      ...document.summaryDocument,
      id: response.id,
      progress: response.progress,
      duration: response.duration,
      name: response.documentName,
      releaseDateTime: response.releaseDateTime,
      url: response.url
    }
  };
};

/**
 * Generates an array of season options for a dropdown input.
 *
 * @param {string[]} podcastSeasons - An array of season names as strings.
 * @returns {Array<{ label: string; value: string }>} An array of objects,
 * each containing a `label` and `value` for use in the dropdown input.
 * The label is the original season name, while the value is the number
 * of the season (excluding "Unknown" seasons, which are handled
 * separately and assigned a predefined value).
 */
export const getSeasonOptions = (podcastSeasons: string[]) => {
  return podcastSeasons.map((season) => {
    return {
      label: season,
      value:
        season !== 'Unknown'
          ? season.substring(7, season.length)
          : config.unknownSeason
    };
  });
};

export const shouldEnableAutoPlay = (userFields: UserFieldsType) => {
  return (
    ((userFields?.auto_play_serial_dont_ask === undefined ||
      !userFields?.auto_play_serial_dont_ask) &&
      (sessionStorage.getItem('auto_play_serial') === null ||
        sessionStorage.getItem('auto_play_serial') === 'true')) ||
    (userFields?.auto_play_serial_dont_ask && userFields?.auto_play_serial) ||
    (userFields?.auto_play_serial_dont_ask &&
      (userFields?.auto_play_serial === undefined ||
        !userFields?.auto_play_serial) &&
      sessionStorage.getItem('auto_play_serial') === 'true')
  );
};

/**
 * Determines whether the "Go to Document" button should be displayed for an document.
 *
 * The button will be displayed if:
 * - The podcast is an enterprise podcast (`enterpriseType` is 1 or `isOwner` is true).
 * - There is either a valid `documentUrl` provided, or the document has a summary,
 *   which typically indicates that a document is associated with it.
 *
 * @param enterpriseType - Indicates whether the podcast is an enterprise podcast.
 * @param isOwner - Indicates whether the podcast is an enterprise podcast.
 * @param documentUrl - The URL of the document, which can be null or an empty string.
 * @param summaryDocument - The summary document, which can be null.
 * @returns `true` if the "Go to Document" button should be displayed; otherwise, `false`.
 */
export const shouldShowDocumentLink = (
  enterpriseType: number,
  isOwner: boolean,
  documentUrl: string | null,
  summaryDocument: SummaryDocument | null
): boolean => {
  return (
    (enterpriseType === 1 || isOwner) &&
    (documentUrl?.trim() !== '' || summaryDocument !== null)
  );
};
